import {
  CPXValueOUT,
  MaterialFlowStatusEnum,
  ObjectUpdateData,
} from "api-client";
import clsx from "clsx";
import Input from "components/Input";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import DropdownSelect, { SelectOption } from "components/DropdownSelect";
import { BPEStore } from "features/BuildingProductEditor/ProductEditorStore";
import useMappingEditor from "features/MappingEditor/useMappingEditor";
import { dynamicTableStore } from "features/DynamicTable/DynamicTableStore";

const EditObjectPopup = () => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const { mappingToFile, mappingToObjects } = useMappingEditor();
  const { openEditObjectPopup } = MEStore;
  const isInputType =
    openEditObjectPopup.type === "string" ||
    openEditObjectPopup.type === "number";
  const isNumericProperty = openEditObjectPopup.type === "number";
  const [options, setOptions] = React.useState<SelectOption[]>([]);
  const [defaultOption, setDefaultOption] = React.useState<SelectOption>();
  const [defaultValue, setDefaultValue] = React.useState<string>();
  const [hasMultipleValues, setHasMultipleValues] = React.useState(false);
  const [validNumericInput, setValidNumericInput] = React.useState(true);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleObjectsUpdateData = async (
    object_update_data: ObjectUpdateData
  ) => {
    if (MEStore.selectAllChecked) {
      await mappingToFile("update_objects", null, object_update_data);
    } else {
      await mappingToObjects("update_objects", null, null, object_update_data);
    }

    MEStore.setOpenBulkActionsMenu(false);
    MEStore.closeEditObjectPopup();
  };

  const handleKeyUp = (
    value: string | undefined,
    keyboardKey: string,
    validity?: ValidityState
  ) => {
    const validInput = !validity?.badInput;
    setValidNumericInput(validInput);

    if (isNumericProperty && !validInput) {
      return;
    }

    if (keyboardKey === "Enter" && openEditObjectPopup.updateKey) {
      handleObjectsUpdateData({
        [openEditObjectPopup.updateKey]: value || null,
      });
    }
  };

  const handleSelectOption = (option: SelectOption | null) => {
    if (openEditObjectPopup.updateKey) {
      handleObjectsUpdateData({
        [openEditObjectPopup.updateKey]: option?.id ?? null,
      });
    }
  };

  function getCPXOptions(column_key: string) {
    const options = BPEStore.optionsLists[column_key].map((e) => ({
      id: String(e.id),
      name: String(e.name),
      extra: `Score ${Number((e as CPXValueOUT).numeric_value) * 100}`,
    }));

    setOptions(options);
    getDefaultOption(options, BPEStore.optionsLists[column_key]);
  }

  const materialFlowStatusOptions = [
    {
      id: MaterialFlowStatusEnum.Hinzugefgt,
      name: MaterialFlowStatusEnum.Hinzugefgt,
      tooltip: t("mappingEditor.addMenuTooltip"),
    },
    {
      id: MaterialFlowStatusEnum.Verbleibt,
      name: MaterialFlowStatusEnum.Verbleibt,
      tooltip: t("mappingEditor.remainMenuTooltip"),
    },
    {
      id: MaterialFlowStatusEnum.Entfernt,
      name: MaterialFlowStatusEnum.Entfernt,
      tooltip: t("mappingEditor.removeMenuTooltip"),
    },
    {
      id: MaterialFlowStatusEnum.TemporrHinzugefgt,
      name: MaterialFlowStatusEnum.TemporrHinzugefgt,
      tooltip: t("mappingEditor.tempMenuTooltip"),
    },
  ] as SelectOption[];

  const getMaterailFlowStatusOptions = () => {
    setOptions(materialFlowStatusOptions);
    getDefaultOption(materialFlowStatusOptions, materialFlowStatusOptions);
  };

  function getDefaultOption(
    selectOptions: SelectOption[],
    listOptions: (CPXValueOUT | SelectOption)[]
  ) {
    const keyName = openEditObjectPopup.columnKey;
    if (!keyName) return;
    const hasDefault = MEStore.selectedRows.every(
      (rec) =>
        rec.values?.[keyName as keyof typeof rec.values] ===
        MEStore.selectedRows[0].values?.[keyName as keyof typeof rec.values]
    );
    if (hasDefault) {
      const defaultItem = listOptions.find(
        (item) =>
          item.name === MEStore.selectedRows[0].values?.[keyName as never]
      );
      defaultItem
        ? setDefaultOption(
            selectOptions.find((rec) => rec.id === defaultItem.id)
          )
        : setDefaultOption(undefined);
      setHasMultipleValues(false);
    } else {
      setDefaultOption(undefined);
      checkIfMultipleValues(keyName);
    }
  }

  function checkIfMultipleValues(keyName: string) {
    const allAreNull = MEStore.selectedRows.every(
      (rec) => rec.values?.[keyName as keyof typeof rec.values] === null
    );
    setHasMultipleValues(!allAreNull);
  }

  function getDefaultValue() {
    const keyName = openEditObjectPopup.columnKey;
    if (!keyName) return;
    let hasDefault;
    if (MEStore.selectedRows.length > 1) {
      hasDefault = MEStore.selectedRows.every((rec) => {
        const currentValue = rec.values?.[keyName as keyof typeof rec.values];
        const firstItemValue =
          MEStore.selectedRows[0].values?.[keyName as keyof typeof rec.values];

        return currentValue && currentValue === firstItemValue;
      });
    } else {
      hasDefault = true;
    }
    if (hasDefault) {
      setDefaultValue(
        MEStore.selectedRows[0].values?.[keyName as never] ?? undefined
      );
      setHasMultipleValues(false);
    } else {
      setDefaultValue(undefined);
      checkIfMultipleValues(keyName);
    }
  }

  function switchOptions() {
    switch (MEStore.openEditObjectPopup.updateKey) {
      case "material_flow_status":
        getMaterailFlowStatusOptions();
        break;
      case "cpx_preuse_id":
        getCPXOptions("preuse_qualification");
        break;
      case "cpx_deconstructability_id":
        getCPXOptions("deconstructability_qualification");
        break;
      case "cpx_reusability_id":
        getCPXOptions("reusability_qualification");
        break;
      case "cpx_separability_id":
        getCPXOptions("separability_qualification");
        break;
      default:
        setOptions([]);
        setDefaultOption(undefined);
        getDefaultValue();
    }
  }

  function getFieldName() {
    const founded = dynamicTableStore.dynamicColumns.find(
      (item) => item.key_name === openEditObjectPopup.columnKey
    );
    const name = isEng ? founded?.friendly_name : founded?.friendly_name_de;
    return name?.split("(")[0].trim();
  }

  React.useEffect(() => {
    switchOptions();
    if (isInputType) {
      inputRef.current?.focus();
    }
    return () => MEStore.closeEditObjectPopup();
  }, [openEditObjectPopup.state]);

  React.useEffect(() => {
    const handleCtrlA = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
        const active = inputRef.current;

        if (active) {
          (active as HTMLInputElement | HTMLTextAreaElement).select();
        }
        e.stopPropagation();
        e.preventDefault();
      }
    };

    document.addEventListener("keydown", handleCtrlA);
    return () => {
      document.removeEventListener("keydown", handleCtrlA);
    };
  }, []);

  return (
    <div
      className={clsx(
        "absolute bottom-[90px] h-[76px] min-w-[266px] bg-white shadow-xl border border-gray-200",
        "rounded-md p-2 relative"
      )}
    >
      {isInputType ? (
        <Input
          type={openEditObjectPopup.type === "string" ? "string" : "number"}
          noMargin
          min={isNumericProperty ? 0 : undefined}
          value={defaultValue}
          onChange={(value) => {
            setDefaultValue(value);
          }}
          onKeyup={handleKeyUp}
          shouldShowValidationError={() => {
            return isNumericProperty && !validNumericInput;
          }}
          placeholder={
            hasMultipleValues
              ? t("mappingEditor.differentValues")
              : t("mappingEditor.pleaseEnterValue", {
                  property_name: getFieldName(),
                })
          }
          ref={inputRef}
        />
      ) : (
        <DropdownSelect
          options={options}
          onChange={(option) => handleSelectOption(option)}
          direction="up"
          clearOptionLabel={t("mappingEditor.removeSelection")}
          clearable={openEditObjectPopup.updateKey !== "material_flow_status"}
          defaultValue={defaultOption}
          placeholder={
            hasMultipleValues
              ? t("mappingEditor.differentValues")
              : t("mappingEditor.selectProperty", {
                  property_name: getFieldName(),
                })
          }
        />
      )}
      <span className="text-xs font-normal text-gray-500">
        {isInputType
          ? t("mappingEditor.pressEnterToSave")
          : t("mappingEditor.selectValueForSaving")}
      </span>
    </div>
  );
};

export default observer(EditObjectPopup);
